import { IconHistory } from "@tabler/icons-react"
import { Sheet, SheetContent } from "design-system/atoms/Sheet"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { ChartConfig } from "design-system/molecules/Chart"
import { FilterMenu, FilterMenuField } from "design-system/molecules/FilterMenu"
import { Section } from "design-system/molecules/Section"
import { StackedActivityChart } from "design-system/organisms/insights/StackedActivityChart"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilityExecutionStatus,
  CapabilityResponseDataSchema,
  ConversationTypeEnum,
} from "schema"
import z from "zod"
import { useCapabilitiesDictionary } from "../../hooks/useCapabilitiesDictionary"
import { useCapabilityExecutionsAnalyticsQuery } from "../../hooks/useCapabilityExecutionsAnalyticsQuery"
import { useCapabilityExecutionsQuery } from "../../hooks/useCapabilityExecutionsQuery"
import { CapabilityExecutionsFilters } from "../../lib/capabilityExecutionsQuery"
import { ExecutionItem } from "./ExecutionItem"

const ITEMS_PER_PAGE = 10
const RANGE_DAYS = [7, 30] as const

type CapabilityExecutionsSheetProps = {
  capability: z.infer<typeof CapabilityResponseDataSchema>
  onOpenChange: (open: boolean) => void
  campaignId?: string
}

export function CapabilityExecutionsSheet({
  capability,
  onOpenChange,
  campaignId: initialCampaignId,
}: CapabilityExecutionsSheetProps) {
  const { t } = useTranslation()
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const labels = useFilterBarLabels()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number | undefined>()
  const [status, setStatus] = useState<CapabilityExecutionStatus>()
  const [campaignId, setCampaignId] = useState<string | undefined>(initialCampaignId)
  const [conversationType, setConversationType] =
    useState<CapabilityExecutionsFilters["conversationType"]>()
  const [rangeDays, setRangeDays] = useState<CapabilityExecutionsFilters["rangeDays"]>(7)

  const filters = useMemo<CapabilityExecutionsFilters>(
    () => ({ campaignId, status, conversationType, rangeDays }),
    [campaignId, conversationType, rangeDays, status],
  )

  const { data: campaigns } = useCampaignsQuery({
    query: {
      filterBy: { archived: false },
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const { data: response, isLoading } = useCapabilityExecutionsQuery({
    id: capability.id,
    page,
    total: ITEMS_PER_PAGE,
    filters,
  })

  const { data: analytics, isPending: isAnalyticsPending } = useCapabilityExecutionsAnalyticsQuery({
    id: capability.id,
    filters,
  })

  const executions = response?.data ?? []
  const responseCount = response?.pageInfo?.count

  useEffect(() => {
    if (responseCount !== undefined) {
      setCount(responseCount)
    }
  }, [responseCount])

  useEffect(() => {
    setPage(1)
  }, [filters])

  const capabilityName =
    capabilitiesDictionary[capability.identifier as keyof typeof capabilitiesDictionary]?.name ?? ""

  const hasData = !isLoading && executions && executions.length > 0
  const totalPages = count ? Math.ceil(count / ITEMS_PER_PAGE) : undefined

  const chartConfig = {
    top1: { label: t("capabilities.executions.success"), color: "var(--success)" },
    top2: { label: t("capabilities.executions.unsuccess"), color: "var(--testing-1)" },
    top3: { label: t("capabilities.executions.cached"), color: "var(--muted-foreground)" },
    top4: { label: t("capabilities.executions.error"), color: "var(--destructive)" },
    top5: { label: t("capabilities.executions.skipped"), color: "var(--warning)" },
  } satisfies ChartConfig

  const chartData = useMemo(
    () =>
      analytics?.series.map((day) => ({
        date: day.date,
        top1: day.Success,
        top2: day.Unsuccess,
        top3: day.Cached,
        top4: day.Error,
        top5: day.Skipped,
      })),
    [analytics?.series],
  )

  const successRatePercent = Math.round((analytics?.successRate ?? 0) * 100)

  const fields: FilterMenuField[] = [
    {
      key: "status",
      label: t("capabilities.executions.filters.status"),
      value: status,
      onChange: (value) =>
        setStatus(Object.values(CapabilityExecutionStatus).find((option) => option === value)),
      options: [
        { value: CapabilityExecutionStatus.Success, label: t("capabilities.executions.success") },
        {
          value: CapabilityExecutionStatus.Unsuccess,
          label: t("capabilities.executions.unsuccess"),
        },
        { value: CapabilityExecutionStatus.Cached, label: t("capabilities.executions.cached") },
        { value: CapabilityExecutionStatus.Error, label: t("capabilities.executions.error") },
        { value: CapabilityExecutionStatus.Skipped, label: t("capabilities.executions.skipped") },
      ],
    },
    {
      key: "campaignId",
      label: t("capabilities.executions.filters.campaign"),
      value: campaignId,
      onChange: setCampaignId,
      options: (campaigns ?? []).map((campaign) => ({
        value: campaign.id,
        label: campaign.name?.trim() ? campaign.name : t("campaigns.untitled"),
      })),
    },
    {
      key: "conversationType",
      label: t("capabilities.executions.filters.conversationType"),
      value: conversationType,
      onChange: (value) =>
        setConversationType(Object.values(ConversationTypeEnum).find((option) => option === value)),
      options: [
        { value: ConversationTypeEnum.Live, label: t("capabilities.executions.filters.real") },
        {
          value: ConversationTypeEnum.Testing,
          label: t("capabilities.executions.filters.playground"),
        },
      ],
    },
    {
      key: "rangeDays",
      label: t("capabilities.executions.filters.range"),
      value: rangeDays === 30 ? "30" : undefined,
      onChange: (value) => setRangeDays(RANGE_DAYS.find((days) => String(days) === value) ?? 7),
      options: [{ value: "30", label: t("capabilities.executions.filters.range30d") }],
    },
  ]

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-4xl flex flex-col gap-0 p-0">
        <div className="bg-muted border-b px-6 py-3">
          <Section
            title={t("capabilities.executions.title")}
            description={capabilityName}
            titleClassName="text-lg"
          />
        </div>

        <div className="px-6 py-3">
          <FilterMenu fields={fields} labels={labels} />
        </div>

        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-4">
          <Card className="p-4">
            <div className="grid grid-cols-[110px_1fr] gap-4 items-center">
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger slick className="text-xs text-muted-foreground">
                      {t("capabilities.executions.successRate")}
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("capabilities.executions.successRateTooltip")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <p className="text-3xl font-semibold leading-none mt-1">
                  {isAnalyticsPending ? "—" : `${successRatePercent}%`}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t("capabilities.executions.successRateDetail", {
                    successful: analytics?.successfulConversations ?? 0,
                    total: analytics?.totalConversations ?? 0,
                  })}
                </p>
              </div>
              <StackedActivityChart
                data={chartData}
                isPending={isAnalyticsPending}
                chartConfig={chartConfig}
                hideZeroTooltipEntries
                chartClassName="h-[120px]"
                noDataT={{
                  title: t("capabilities.executions.chartEmpty.title"),
                  description: t("capabilities.executions.chartEmpty.description"),
                }}
              />
            </div>
          </Card>

          {isLoading && <Loading />}
          {!hasData && !isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
              <IconHistory
                className="text-muted-foreground opacity-50 size-12"
                strokeWidth={1.25}
              />
              <p className="text-base text-muted-foreground">
                {t("capabilities.executions.empty")}
              </p>
            </div>
          )}
          {hasData && (
            <div className="flex flex-col gap-4">
              {executions.map((execution) => (
                <ExecutionItem key={execution.id} capability={capability} execution={execution} />
              ))}
            </div>
          )}
        </div>

        {hasData && (
          <div className="border-t p-4">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              count={count}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
